import type { LeadWithValuation } from '@/types'
import { exportFileName } from './common'

const MESI_ITALIANI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

function formatCurrencyPlain(value: number): string {
  if (value == null || isNaN(value)) return '€ 0'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Funzioni di calcolo statistiche condivise
export function computeFunnel(leads: LeadWithValuation[]) {
  const total = leads.length || 1
  const counts: Record<string, number> = { new: 0, contacted: 0, callback: 0, acquired: 0 }
  leads.forEach(l => { if (counts[l.status] !== undefined) counts[l.status]++ })
  return [
    { label: 'Nuovi', count: counts.new, pct: (counts.new / total) * 100 },
    { label: 'Contattati', count: counts.contacted, pct: (counts.contacted / total) * 100 },
    { label: 'Da richiamare', count: counts.callback, pct: (counts.callback / total) * 100 },
    { label: 'Acquisiti', count: counts.acquired, pct: (counts.acquired / total) * 100 },
  ]
}

export function computeAcquiredStats(leads: LeadWithValuation[]) {
  const acquired = leads.filter(l => l.status === 'acquired').length
  const rate = leads.length > 0 ? Math.round((acquired / leads.length) * 100) : 0
  return { count: acquired, rate }
}

export function computePriceStats(leads: LeadWithValuation[]) {
  const prices = leads.filter(l => l.valuation?.price_per_sqm).map(l => l.valuation!.price_per_sqm)
  if (prices.length === 0) return { avg: 0, min: 0, max: 0 }
  return {
    avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
    min: Math.min(...prices),
    max: Math.max(...prices),
  }
}

export function computeZoneStats(leads: LeadWithValuation[]) {
  const groups: Record<string, { count: number; totalValue: number; totalPricePerSqm: number }> = {}
  leads.forEach(l => {
    if (!l.valuation?.zone_name_csv) return
    const zone = l.valuation.zone_name_csv
    if (!groups[zone]) groups[zone] = { count: 0, totalValue: 0, totalPricePerSqm: 0 }
    groups[zone].count++
    groups[zone].totalValue += l.valuation.estimated_value
    groups[zone].totalPricePerSqm += l.valuation.price_per_sqm
  })
  return Object.entries(groups)
    .map(([zone, data]) => ({
      zone,
      count: data.count,
      avgValue: data.totalValue / data.count,
      avgPricePerSqm: data.totalPricePerSqm / data.count,
    }))
    .sort((a, b) => b.count - a.count)
}

export function computeTypeStats(leads: LeadWithValuation[]) {
  const groups: Record<string, { count: number; totalArea: number; totalValue: number }> = {}
  leads.forEach(l => {
    if (!l.valuation?.property_type) return
    const type = l.valuation.property_type
    if (!groups[type]) groups[type] = { count: 0, totalArea: 0, totalValue: 0 }
    groups[type].count++
    groups[type].totalArea += l.valuation.area
    groups[type].totalValue += l.valuation.estimated_value
  })
  return Object.entries(groups)
    .map(([type, data]) => ({
      type,
      count: data.count,
      avgArea: Math.round(data.totalArea / data.count),
      avgValue: data.totalValue / data.count,
    }))
    .sort((a, b) => b.count - a.count)
}

export function computeMonthlyStats(leads: LeadWithValuation[]) {
  const groups: Record<string, number> = {}
  leads.forEach(l => {
    const d = new Date(l.created_at)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    groups[key] = (groups[key] || 0) + 1
  })
  const sorted = Object.entries(groups).sort(([a], [b]) => b.localeCompare(a))
  return sorted.map(([key, count], i) => {
    const [year, month] = key.split('-').map(Number)
    const prevCount = i < sorted.length - 1 ? sorted[i + 1][1] : null
    let trend: 'up' | 'down' | 'same' = 'same'
    if (prevCount !== null) {
      if (count > prevCount) trend = 'up'
      else if (count < prevCount) trend = 'down'
    }
    return { key, label: `${MESI_ITALIANI[month - 1]} ${year}`, count, trend }
  })
}

type DocWithAutoTable = InstanceType<typeof import('jspdf').default> & {
  lastAutoTable: { finalY: number }
}

export async function exportStatsPDF(leads: LeadWithValuation[]) {
  const { default: jsPDF } = await import('jspdf')
  const { default: autoTable } = await import('jspdf-autotable')

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 16
  let y = 20

  // Header
  doc.setFillColor(37, 99, 235)
  doc.rect(0, 0, pageWidth, 30, 'F')
  doc.setTextColor(255)
  doc.setFontSize(18)
  doc.text('Valutazioni Immobiliari Online - Report Statistiche', margin, 14)
  doc.setFontSize(9)
  doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')} - ${leads.length} lead totali`, margin, 22)
  doc.setTextColor(0)
  y = 40

  // KPI: Funnel
  const funnel = computeFunnel(leads)
  doc.setFontSize(12)
  doc.setTextColor(37, 99, 235)
  doc.text('Funnel Lead', margin, y)
  y += 6

  autoTable(doc, {
    startY: y,
    head: [['Stato', 'Conteggio', 'Percentuale']],
    body: funnel.map(f => [f.label, f.count.toString(), `${Math.round(f.pct)}%`]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    margin: { left: margin, right: margin },
    theme: 'grid',
  })
  y = (doc as DocWithAutoTable).lastAutoTable.finalY + 10

  // KPI box
  const acquired = computeAcquiredStats(leads)
  const price = computePriceStats(leads)
  doc.setFillColor(243, 244, 246)
  doc.roundedRect(margin, y, pageWidth - margin * 2, 18, 2, 2, 'F')
  doc.setFontSize(9)
  doc.setTextColor(80)
  doc.text(`Tasso acquisizione: ${acquired.rate}% (${acquired.count} su ${leads.length})`, margin + 6, y + 7)
  doc.text(`Prezzo medio/mq: ${formatCurrencyPlain(price.avg)} (min: ${formatCurrencyPlain(price.min)}, max: ${formatCurrencyPlain(price.max)})`, margin + 6, y + 13)
  y += 26

  // Zone
  const zones = computeZoneStats(leads)
  if (zones.length > 0) {
    doc.setFontSize(12)
    doc.setTextColor(37, 99, 235)
    doc.text('Zone più richieste', margin, y)
    y += 6

    autoTable(doc, {
      startY: y,
      head: [['Zona', 'Lead', 'Valore Medio', 'Prezzo/mq']],
      body: zones.map(z => [
        z.zone,
        z.count.toString(),
        formatCurrencyPlain(z.avgValue),
        formatCurrencyPlain(z.avgPricePerSqm),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: margin, right: margin },
      theme: 'grid',
    })
    y = (doc as DocWithAutoTable).lastAutoTable.finalY + 10
  }

  // Tipologia
  const types = computeTypeStats(leads)
  if (types.length > 0) {
    doc.setFontSize(12)
    doc.setTextColor(37, 99, 235)
    doc.text('Distribuzione Tipologia', margin, y)
    y += 6

    autoTable(doc, {
      startY: y,
      head: [['Tipo', 'Lead', 'Sup. Media', 'Valore Medio']],
      body: types.map(t => [
        t.type,
        t.count.toString(),
        `${t.avgArea} mq`,
        formatCurrencyPlain(t.avgValue),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: margin, right: margin },
      theme: 'grid',
    })
    y = (doc as DocWithAutoTable).lastAutoTable.finalY + 10
  }

  // Monthly
  const monthly = computeMonthlyStats(leads)
  if (monthly.length > 0) {
    if (y > 240) { doc.addPage(); y = 20 }

    doc.setFontSize(12)
    doc.setTextColor(37, 99, 235)
    doc.text('Lead nel tempo', margin, y)
    y += 6

    autoTable(doc, {
      startY: y,
      head: [['Mese', 'Lead', 'Trend']],
      body: monthly.map(m => [
        m.label,
        m.count.toString(),
        m.trend === 'up' ? '↑' : m.trend === 'down' ? '↓' : '—',
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: margin, right: margin },
      theme: 'grid',
    })
  }

  // Footer
  doc.setFontSize(7)
  doc.setTextColor(150)
  doc.text(
    'Documento generato automaticamente da Valutazioni Immobiliari Online.',
    margin,
    doc.internal.pageSize.getHeight() - 10
  )

  doc.save(exportFileName('statistiche', 'pdf'))
}
